import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { getMessage, getLiveMessage, sendMessage, ComposePayload } from '../api/mailbox';
import { getMailAccounts, getAliases } from '../api/auth';
import { formatSendError, parseAddressList, parseFromAddress } from '../utils/emailAddress';
import GpgPassphraseDialog from '../components/GpgPassphraseDialog';
import 'react-quill/dist/quill.snow.css';

// Lazy-load Quill (rich HTML editor)
const ReactQuill = React.lazy(() => import('react-quill'));

export default function ComposePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const replyToId = searchParams.get('reply');
  const forwardId = searchParams.get('forward');
  const replyAccount = searchParams.get('replyAccount');
  const replyFolder = searchParams.get('replyFolder');
  const replyUid = searchParams.get('replyUid');
  const forwardAccount = searchParams.get('forwardAccount');
  const forwardFolder = searchParams.get('forwardFolder');
  const forwardUid = searchParams.get('forwardUid');

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [inReplyTo, setInReplyTo] = useState('');
  const [references, setReferences] = useState('');
  const [sign, setSign] = useState(false);
  const [encrypt, setEncrypt] = useState(false);
  const [attachPublicKey, setAttachPublicKey] = useState(false);
  const [useHtml, setUseHtml] = useState(true);
  const [fromKey, setFromKey] = useState(''); // "account:ID" or "alias:ID"
  const [error, setError] = useState('');
  const [passphraseOpen, setPassphraseOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<ComposePayload | null>(null);

  const liveReply =
    replyAccount && replyFolder && replyUid
      ? {
          account: parseInt(replyAccount, 10),
          folder: replyFolder,
          uid: parseInt(replyUid, 10),
        }
      : null;
  const liveForward =
    forwardAccount && forwardFolder && forwardUid
      ? {
          account: parseInt(forwardAccount, 10),
          folder: forwardFolder,
          uid: parseInt(forwardUid, 10),
        }
      : null;

  const referenceId = replyToId || forwardId;
  const { data: refMsgDb } = useQuery({
    queryKey: ['message', referenceId],
    queryFn: () => getMessage(parseInt(referenceId!, 10)),
    enabled: !!referenceId,
  });

  const liveRef = liveReply || liveForward;
  const { data: refMsgLive } = useQuery({
    queryKey: ['live-message', liveRef?.account, liveRef?.folder, liveRef?.uid],
    queryFn: () =>
      getLiveMessage({
        account: liveRef!.account,
        folder: liveRef!.folder,
        uid: liveRef!.uid,
      }),
    enabled: Boolean(liveRef),
  });

  const refMsg = refMsgLive || refMsgDb;
  const isReply = Boolean(replyToId || liveReply);
  const isForward = Boolean(forwardId || liveForward);

  const { data: accountsData } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: getMailAccounts,
  });
  const accounts = accountsData?.results || [];

  const { data: aliasesData } = useQuery({
    queryKey: ['aliases'],
    queryFn: () => getAliases(),
  });
  const aliases = aliasesData?.results || [];

  useEffect(() => {
    if (fromKey || accounts.length === 0) return;
    const defAlias = aliases.find((a) => a.is_default);
    if (defAlias) setFromKey(`alias:${defAlias.id}`);
    else {
      const primary = accounts.find((a) => a.is_primary) || accounts[0];
      if (primary) setFromKey(`account:${primary.id}`);
    }
  }, [accounts, aliases, fromKey]);

  useEffect(() => {
    if (!refMsg) return;
    if (isReply) {
      const replyEmail = parseFromAddress(refMsg.from_address).email || refMsg.from_address;
      setTo(replyEmail);
      setSubject(refMsg.subject.startsWith('Re:') ? refMsg.subject : `Re: ${refMsg.subject}`);
      setInReplyTo(refMsg.message_id);
      setReferences(
        [refMsg.references, refMsg.message_id].filter(Boolean).join(' ')
      );
      setBodyHtml(
        `<br/><br/><blockquote>${refMsg.body_html || refMsg.body_text}</blockquote>`
      );
    } else if (isForward) {
      setSubject(refMsg.subject.startsWith('Fwd:') ? refMsg.subject : `Fwd: ${refMsg.subject}`);
      setBodyHtml(
        `<br/><br/><blockquote>${refMsg.body_html || refMsg.body_text}</blockquote>`
      );
    }
  }, [refMsg, isReply, isForward]);

  const sendMutation = useMutation({
    mutationFn: (payload: ComposePayload) => sendMessage(payload),
    onSuccess: (data) => {
      if (data?.sent_warning || data?.saved_to_sent === false) {
        setError(
          data?.sent_warning
            ? `Sent, but not saved to Sent folder: ${data.sent_warning}`
            : 'Sent, but a copy was not saved to your Sent folder.'
        );
        return;
      }
      navigate(-1);
    },
    onError: (err: unknown, payload) =>
      setError(formatSendError(err, Boolean(payload?.sign || payload?.encrypt))),
  });

  const handleSend = () => {
    setError('');
    if (!to.trim()) { setError('To field is required.'); return; }
    if (!fromKey) { setError('No From address selected.'); return; }
    const [kind, idStr] = fromKey.split(':');
    const id = parseInt(idStr, 10);
    let account_id: number | undefined;
    let alias_id: number | undefined;
    if (kind === 'alias') {
      const alias = aliases.find((a) => a.id === id);
      account_id = alias?.account;
      alias_id = id;
    } else {
      account_id = id;
    }
    if (!account_id) { setError('No mail account configured.'); return; }
    const toList = parseAddressList(to);
    if (!toList.length) {
      setError('To field must contain a valid email address.');
      return;
    }
    const payload: ComposePayload = {
      account_id,
      alias_id,
      to: toList,
      cc: parseAddressList(cc),
      bcc: parseAddressList(bcc),
      subject,
      body_html: useHtml ? bodyHtml : '',
      body_text: useHtml ? '' : bodyHtml,
      in_reply_to: inReplyTo || undefined,
      references: references || undefined,
      sign,
      encrypt,
      attach_public_key: attachPublicKey || undefined,
    };
    if (sign || encrypt) {
      setPendingPayload(payload);
      setPassphraseOpen(true);
      return;
    }
    sendMutation.mutate(payload);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Tooltip title="Discard">
          <IconButton onClick={() => navigate(-1)} aria-label="Discard">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
          {replyToId || liveReply ? 'Reply' : forwardId || liveForward ? 'Forward' : 'New Message'}
        </Typography>
        <Button
          variant="contained"
          startIcon={sendMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sendMutation.isPending}
        >
          Send
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={1}>
        <Stack spacing={2}>
          <TextField
            select
            label="From"
            fullWidth
            value={fromKey}
            onChange={(e) => setFromKey(e.target.value)}
            inputProps={{ 'aria-label': 'From' }}
          >
            {accounts.map((a) => (
              <MenuItem key={`account:${a.id}`} value={`account:${a.id}`}>
                {a.name ? `${a.name} <${a.email_address}>` : a.email_address}
              </MenuItem>
            ))}
            {aliases.map((a) => (
              <MenuItem key={`alias:${a.id}`} value={`alias:${a.id}`}>
                {a.display_name ? `${a.display_name} <${a.email_address}>` : a.email_address}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="To"
            fullWidth
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="recipient@example.com, ..."
            inputProps={{ 'aria-label': 'To' }}
          />
          <TextField
            label="Cc"
            fullWidth
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            inputProps={{ 'aria-label': 'Cc' }}
          />
          <TextField
            label="Bcc"
            fullWidth
            value={bcc}
            onChange={(e) => setBcc(e.target.value)}
            inputProps={{ 'aria-label': 'Bcc' }}
          />
          <TextField
            label="Subject"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            inputProps={{ 'aria-label': 'Subject' }}
          />

          <Divider />

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={useHtml} onChange={(e) => setUseHtml(e.target.checked)} />}
              label="HTML"
            />
            <FormControlLabel
              control={<Switch checked={sign} onChange={(e) => setSign(e.target.checked)} />}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <VerifiedIcon fontSize="small" />
                  <span>Sign</span>
                </Stack>
              }
            />
            <FormControlLabel
              control={<Switch checked={encrypt} onChange={(e) => setEncrypt(e.target.checked)} />}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LockIcon fontSize="small" />
                  <span>Encrypt</span>
                </Stack>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={attachPublicKey}
                  onChange={(e) => setAttachPublicKey(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <VpnKeyIcon fontSize="small" />
                  <span>Attach my public key</span>
                </Stack>
              }
            />
          </Stack>

          {useHtml ? (
            <React.Suspense fallback={<CircularProgress />}>
              <Box
                sx={{
                  '& .ql-toolbar.ql-snow': {
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                    borderColor: 'divider',
                    flexWrap: 'wrap',
                  },
                  '& .ql-container.ql-snow': {
                    borderBottomLeftRadius: 4,
                    borderBottomRightRadius: 4,
                    borderColor: 'divider',
                    minHeight: 300,
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                  },
                  '& .ql-editor': {
                    minHeight: 300,
                  },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={bodyHtml}
                  onChange={setBodyHtml}
                  placeholder="Write your message..."
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['blockquote', 'code-block'],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                />
              </Box>
            </React.Suspense>
          ) : (
            <TextField
              multiline
              minRows={12}
              fullWidth
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              placeholder="Write your message..."
              inputProps={{ 'aria-label': 'Message body' }}
            />
          )}
        </Stack>
      </Paper>

      <GpgPassphraseDialog
        open={passphraseOpen}
        title={encrypt ? 'Encrypt message' : 'Sign message'}
        description={
          encrypt
            ? 'Enter your secret-key passphrase (needed if you also sign, or if your key is protected). Recipients must have your public key / you must have theirs.'
            : 'Enter the passphrase for your secret key to sign this message.'
        }
        confirmLabel="Send"
        busy={sendMutation.isPending}
        error={error || null}
        onCancel={() => {
          setPassphraseOpen(false);
          setPendingPayload(null);
        }}
        onConfirm={(passphrase) => {
          if (!pendingPayload) return;
          sendMutation.mutate(
            { ...pendingPayload, gpg_passphrase: passphrase },
            {
              onSettled: () => {
                setPassphraseOpen(false);
                setPendingPayload(null);
              },
            }
          );
        }}
      />
    </Box>
  );
}
